// Recolors a single-ink alpha PNG (a graphic pattern) in the browser.
// The PNG's alpha channel is the pattern; its RGB is ignored. Preview is pure
// CSS (mask-image over a ground layer); export draws the same layers on a
// canvas at native resolution and hands back a PNG blob. Shared by the
// catalog cards and the Design Your Own barrel.

export const PRINT_MODES = Object.freeze({
  DIGITAL_PRINT: 'digital_print',
  ETCHED_GLASS: 'etched_glass',
});

export const ETCH_TARGETS = Object.freeze({
  PATTERN: 'pattern',
  BACKGROUND: 'background',
});

// Static opacity presets for the printed layers.
export const OPACITY_LEVELS = Object.freeze([
  { level: 'I', alpha: 0.1 },
  { level: 'II', alpha: 0.2 },
  { level: 'III', alpha: 0.5 },
  { level: 'IV', alpha: 1 },
]);

// Most opaque is where the designer starts. Held as an index, so editing the
// presets cannot leave the default alpha off the list.
export const DEFAULT_LEVEL_INDEX = OPACITY_LEVELS.length - 1;

const DEFAULT_ALPHA = OPACITY_LEVELS[DEFAULT_LEVEL_INDEX].alpha;

// Etching has no color: frost on clear glass. Values are placeholders until
// design supplies the intended look.
export const ETCH_PALETTE = Object.freeze({
  frost: '#f3f3f1',
  frostAlpha: 0.9,
});

export const DEFAULT_SELECTION = Object.freeze({
  mode: PRINT_MODES.DIGITAL_PRINT,
  // null means "Clear (no color)" for either layer. Matches the Walter
  // serializer's GRAPHIC_PATTERN_DESIGNER_DEFAULT_FOREGROUND.
  foreground: '#ff9932',
  foregroundAlpha: DEFAULT_ALPHA,
  background: null,
  backgroundAlpha: DEFAULT_ALPHA,
  // true when the variant itself is an inverse, or the user flipped it.
  inverse: false,
  etchTarget: ETCH_TARGETS.PATTERN,
});

export const PREVIEW_CLASS = 'pattern-preview';

// Turns a selection into the two paint layers plus the effective mask
// direction. `ink` paints through the pattern mask; `ground` is the
// background ink, printed only where the pattern is not (a knock-out), so
// a clear layer leaves the panel showing through there.
export const resolveLayers = selection => {
  const s = { ...DEFAULT_SELECTION, ...selection };
  const etched = s.mode === PRINT_MODES.ETCHED_GLASS;
  const etchFlip = etched && s.etchTarget === ETCH_TARGETS.BACKGROUND;

  if (etched) {
    return {
      ink: { color: ETCH_PALETTE.frost, alpha: ETCH_PALETTE.frostAlpha },
      ground: null,
      inverse: Boolean(s.inverse) !== etchFlip,
    };
  }

  return {
    ink: s.foreground
      ? { color: s.foreground, alpha: s.foregroundAlpha }
      : null,
    ground: s.background
      ? { color: s.background, alpha: s.backgroundAlpha }
      : null,
    inverse: Boolean(s.inverse),
  };
};

// CSS custom properties for a preview element. Apply with style-map in Lit
// or the style prop in React.
export const previewStyle = (imageUrl, selection) => {
  const { ink, ground } = resolveLayers(selection);
  return {
    '--pattern-image': 'url("' + imageUrl + '")',
    '--pattern-ink': ink ? ink.color : 'transparent',
    '--pattern-ink-alpha': ink ? String(ink.alpha) : '0',
    '--pattern-ground': ground ? ground.color : 'transparent',
    '--pattern-ground-alpha': ground ? String(ground.alpha) : '0',
  };
};

export const previewClassName = selection => {
  const { inverse } = resolveLayers(selection);
  const s = { ...DEFAULT_SELECTION, ...selection };
  return [
    PREVIEW_CLASS,
    inverse ? 'is-inverse' : '',
    s.mode === PRINT_MODES.ETCHED_GLASS ? 'is-etched' : '',
  ]
    .filter(Boolean)
    .join(' ');
};

// Markup contract: a container with two absolutely positioned children.
//   <div class="pattern-preview" style="--pattern-image: ...">
//     <div class="pattern-preview__ground"></div>
//     <div class="pattern-preview__ink"></div>
//   </div>
// The host sets the container's size and aspect ratio. mask-size defaults
// to cover so the pattern fills the box; override it for tiled swatches.
// Ink is masked by the pattern, ground by its complement; is-inverse swaps
// the two masks.
export const PREVIEW_CSS = [
  '.pattern-preview {',
  '  position: relative;',
  '  overflow: hidden;',
  '}',
  '.pattern-preview__ground,',
  '.pattern-preview__ink {',
  '  position: absolute;',
  '  inset: 0;',
  '  -webkit-mask-size: var(--pattern-mask-size, cover);',
  '  mask-size: var(--pattern-mask-size, cover);',
  '  -webkit-mask-position: center;',
  '  mask-position: center;',
  '  -webkit-mask-repeat: no-repeat;',
  '  mask-repeat: no-repeat;',
  '}',
  '.pattern-preview__ground {',
  '  background: var(--pattern-ground, transparent);',
  '  opacity: var(--pattern-ground-alpha, 0);',
  '}',
  '.pattern-preview__ink {',
  '  background: var(--pattern-ink, transparent);',
  '  opacity: var(--pattern-ink-alpha, 0);',
  '}',
  '.pattern-preview__ink,',
  '.pattern-preview.is-inverse .pattern-preview__ground {',
  '  -webkit-mask-image: var(--pattern-image);',
  '  mask-image: var(--pattern-image);',
  '}',
  '.pattern-preview__ground,',
  '.pattern-preview.is-inverse .pattern-preview__ink {',
  '  -webkit-mask-image: linear-gradient(#000, #000), var(--pattern-image);',
  '  mask-image: linear-gradient(#000, #000), var(--pattern-image);',
  '  -webkit-mask-composite: xor;',
  '  mask-composite: exclude;',
  '}',
  // Frost is near white, so etched previews need a glass tint behind them.
  '.pattern-preview.is-etched {',
  '  background: var(--pattern-glass, #e3e7e6);',
  '}',
].join('\n');

const loadImage = url =>
  new Promise((resolve, reject) => {
    const image = new Image();
    // Required so the canvas stays exportable. ImageBoss and the assets
    // bucket both send Access-Control-Allow-Origin: *.
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Pattern image failed to load: ' + url));
    image.src = url;
  });

// iOS Safari refuses canvases above 16.7 megapixels; full panels are 18.
export const MAX_CANVAS_PIXELS = 16 * 1024 * 1024;

const fitWithinPixels = (width, height, maxPixels) => {
  const pixels = width * height;
  if (pixels <= maxPixels) return { width, height };
  const scale = Math.sqrt(maxPixels / pixels);
  return {
    width: Math.floor(width * scale),
    height: Math.floor(height * scale),
  };
};

// Draws the recolored pattern at the image's native size (or the size
// given), capped at maxPixels. Resolves with the canvas so callers can
// preview or export.
export const renderToCanvas = async (imageUrl, selection, options = {}) => {
  const image = await loadImage(imageUrl);
  const { width, height } = fitWithinPixels(
    options.width || image.naturalWidth,
    options.height || image.naturalHeight,
    options.maxPixels || MAX_CANVAS_PIXELS
  );
  const { ink, ground, inverse } = resolveLayers(selection);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (ink) {
    ctx.drawImage(image, 0, 0, width, height);
    // source-in keeps the fill where the PNG is opaque; source-out keeps it
    // where the PNG is transparent, which is the inverse pattern.
    ctx.globalCompositeOperation = inverse ? 'source-out' : 'source-in';
    ctx.globalAlpha = ink.alpha;
    ctx.fillStyle = ink.color;
    ctx.fillRect(0, 0, width, height);
  }

  if (ground) {
    // Background ink only where the pattern is not: fill, then knock the
    // pattern out of it before laying it under the ink.
    const groundCanvas = document.createElement('canvas');
    groundCanvas.width = width;
    groundCanvas.height = height;
    const groundCtx = groundCanvas.getContext('2d');
    groundCtx.globalAlpha = ground.alpha;
    groundCtx.fillStyle = ground.color;
    groundCtx.fillRect(0, 0, width, height);
    groundCtx.globalAlpha = 1;
    groundCtx.globalCompositeOperation = inverse
      ? 'destination-in'
      : 'destination-out';
    groundCtx.drawImage(image, 0, 0, width, height);

    ctx.globalCompositeOperation = 'destination-over';
    ctx.globalAlpha = 1;
    ctx.drawImage(groundCanvas, 0, 0);
  }

  if (options.backdrop) {
    ctx.globalCompositeOperation = 'destination-over';
    ctx.globalAlpha = 1;
    ctx.fillStyle = options.backdrop;
    ctx.fillRect(0, 0, width, height);
  }

  return canvas;
};

const canvasToBlob = canvas =>
  new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas export failed'));
    }, 'image/png');
  });

export const exportPng = async (imageUrl, selection, options = {}) => {
  const canvas = await renderToCanvas(imageUrl, selection, options);
  return canvasToBlob(canvas);
};

// Triggers a browser download of a blob. Safari opens PNGs in a new tab
// when the download attribute is ignored; that is acceptable fallback.
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const slug = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

// e.g. dapple-scale-b-inverse-ff9932-on-clear.png
export const configuredFilename = (patternName, variantName, selection) => {
  const { ink, ground } = resolveLayers(selection);
  const color = part => (part ? part.color.replace('#', '') : 'clear');
  return (
    [slug(patternName), slug(variantName), color(ink), 'on', color(ground)]
      .filter(Boolean)
      .join('-') + '.png'
  );
};
